import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { Cookie, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCookieConsent } from "@/hooks/use-cookie-consent";

export function CookieBanner() {
  const { hasDecided, acceptAll, rejectAll, openSettings } = useCookieConsent();
  if (hasDecided) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded-2xl border border-border bg-background/95 p-4 shadow-lg backdrop-blur sm:p-5"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted">
          <Cookie className="h-5 w-5 text-foreground" />
        </div>
        <div className="flex-1 text-sm text-foreground">
          <p className="font-semibold">We use cookies</p>
          <p className="mt-1 text-muted-foreground">
            We use essential cookies to run this site and optional cookies for analytics and
            marketing. See our{" "}
            <Link
              to="/cookie-policy"
              className="underline underline-offset-2 hover:text-foreground"
            >
              cookie policy
            </Link>
            .
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" className="rounded-full" onClick={acceptAll}>
              Accept all
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={rejectAll}>
              Reject optional
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full" onClick={openSettings}>
              <Settings2 className="mr-1.5 h-4 w-4" />
              Customize
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CookieSettingsDialog() {
  const { consent, settingsOpen, setSettingsOpen, save, rejectAll } = useCookieConsent();
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);

  useEffect(() => {
    if (settingsOpen) {
      setAnalytics(consent?.analytics ?? false);
      setMarketing(consent?.marketing ?? false);
    }
  }, [settingsOpen, consent]);

  return (
    <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cookie settings</DialogTitle>
          <DialogDescription>
            Choose which optional cookies you allow. You can change these any time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Row
            title="Strictly necessary"
            description="Required for sign-in, security and core site functionality. Always on."
            checked
            disabled
            onChange={() => {}}
          />
          <Row
            title="Analytics"
            description="Helps us understand how the site is used so we can improve it."
            checked={analytics}
            onChange={setAnalytics}
          />
          <Row
            title="Marketing"
            description="Used to measure campaign performance and tailor offers."
            checked={marketing}
            onChange={setMarketing}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" className="rounded-full" onClick={rejectAll}>
            Reject optional
          </Button>
          <Button className="rounded-full" onClick={() => save({ analytics, marketing })}>
            Save preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

export function CookieSettingsButton({ className }: { className?: string }) {
  const { openSettings } = useCookieConsent();
  return (
    <button
      type="button"
      onClick={openSettings}
      className={
        className ??
        "text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      }
    >
      Cookie settings
    </button>
  );
}
